// UPC/Barcode validation utilities

/**
 * Validates UPC/EAN codes using standard check digit algorithms
 * @param {string} code - The UPC/EAN code to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export function validateUPC(code) {
  if (!code || typeof code !== 'string') return false;
  
  // Handle special case for "n/a"
  if (code.toLowerCase() === 'n/a') return true;
  
  // Remove all non-digit characters
  const digits = code.replace(/[^\d]/g, '');
  
  // Check for valid UPC/EAN lengths
  if (![8, 12, 13, 14].includes(digits.length)) return false;
  
  // Validate based on length
  switch (digits.length) {
    case 8:
      return validateEAN8(digits);
    case 12:
      return validateUPC12(digits);
    case 13:
      return validateEAN13(digits);
    case 14:
      return validateGTIN14(digits);
    default:
      return false;
  }
}

/**
 * Get UPC format description
 * @param {string} code - The UPC code
 * @returns {string} - Description of the UPC format
 */
export function getUPCFormat(code) {
  if (!code) return 'No UPC';
  
  // Handle special case for "n/a"
  if (code.toLowerCase() === 'n/a') return 'Disabled';
  
  const digits = code.replace(/[^\d]/g, '');
  
  switch (digits.length) {
    case 8:
      return validateEAN8(digits) ? 'EAN-8' : 'Invalid EAN-8';
    case 12:
      return validateUPC12(digits) ? 'UPC-A' : 'Invalid UPC-A';
    case 13:
      return validateEAN13(digits) ? 'EAN-13' : 'Invalid EAN-13';
    case 14:
      return validateGTIN14(digits) ? 'GTIN-14' : 'Invalid GTIN-14';
    default:
      return `Invalid (${digits.length} digits)`;
  }
}

/**
 * Format UPC with appropriate spacing for display
 * @param {string} code - The UPC code
 * @returns {string} - Formatted UPC code
 */
export function formatUPC(code) {
  if (!code) return '';
  
  const digits = code.replace(/[^\d]/g, '');
  
  switch (digits.length) {
    case 8:
      // EAN-8: XXXX XXXX
      return digits.replace(/(\d{4})(\d{4})/, '$1 $2');
    case 12:
      // UPC-A: X XXXXX XXXXX X
      return digits.replace(/(\d{1})(\d{5})(\d{5})(\d{1})/, '$1 $2 $3 $4');
    case 13:
      // EAN-13: X XXXXXX XXXXXX X
      return digits.replace(/(\d{1})(\d{6})(\d{6})/, '$1 $2 $3');
    case 14:
      // GTIN-14: X XXXX XXXX XXXX X
      return digits.replace(/(\d{1})(\d{4})(\d{4})(\d{4})(\d{1})/, '$1 $2 $3 $4 $5');
    default:
      return digits;
  }
}

/**
 * Check if UPC is likely problematic for eBay
 * @param {string} code - The UPC code
 * @returns {object} - {isProblematic: boolean, reason: string}
 */
export function checkUPCProblems(code) {
  if (!code || code.trim() === '') {
    return { isProblematic: false, reason: '' };
  }
  
  // Handle special case for "n/a" - not problematic
  if (code.toLowerCase() === 'n/a') {
    return { isProblematic: false, reason: '' };
  }
  
  const digits = code.replace(/[^\d]/g, '');
  
  // Invalid format
  if (!validateUPC(code)) {
    return { 
      isProblematic: true, 
      reason: `Invalid ${getUPCFormat(code)} format - check digit validation failed` 
    };
  }
  
  // Very short codes might be problematic
  if (digits.length < 8) {
    return {
      isProblematic: true,
      reason: `Code too short (${digits.length} digits) - UPC/EAN codes should be 8+ digits`
    };
  }
  
  // All zeros or very simple patterns might be problematic
  if (digits === '0'.repeat(digits.length)) {
    return {
      isProblematic: true,
      reason: 'All zeros - likely placeholder or invalid code'
    };
  }
  
  return { isProblematic: false, reason: '' };
}

// UPC validation algorithms
function validateUPC12(digits) {
  const checksum = calculateUPC12Checksum(digits.slice(0, 11));
  return checksum === parseInt(digits[11]);
}

function validateEAN8(digits) {
  const checksum = calculateEAN8Checksum(digits.slice(0, 7));
  return checksum === parseInt(digits[7]);
}

function validateEAN13(digits) {
  const checksum = calculateEAN13Checksum(digits.slice(0, 12));
  return checksum === parseInt(digits[12]);
}

function validateGTIN14(digits) {
  const checksum = calculateGTIN14Checksum(digits.slice(0, 13));
  return checksum === parseInt(digits[13]);
}

// Checksum calculation functions
function calculateUPC12Checksum(digits) {
  let sum = 0;
  for (let i = 0; i < 11; i++) {
    const digit = parseInt(digits[i]);
    sum += i % 2 === 0 ? digit * 3 : digit;
  }
  return (10 - (sum % 10)) % 10;
}

function calculateEAN8Checksum(digits) {
  let sum = 0;
  for (let i = 0; i < 7; i++) {
    const digit = parseInt(digits[i]);
    sum += i % 2 === 0 ? digit * 3 : digit;
  }
  return (10 - (sum % 10)) % 10;
}

function calculateEAN13Checksum(digits) {
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    const digit = parseInt(digits[i]);
    sum += i % 2 === 0 ? digit : digit * 3;
  }
  return (10 - (sum % 10)) % 10;
}

function calculateGTIN14Checksum(digits) {
  let sum = 0;
  for (let i = 0; i < 13; i++) {
    const digit = parseInt(digits[i]);
    sum += i % 2 === 0 ? digit * 3 : digit;
  }
  return (10 - (sum % 10)) % 10;
}

/**
 * Clean and normalize UPC code
 * @param {string} raw - Raw UPC string
 * @returns {string} - Cleaned UPC with only digits
 */
export function cleanUPC(raw) {
  if (!raw) return '';
  return raw.replace(/[^\d]/g, '');
}